import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  fetchHomePage,
  findSection,
  HOME_SECTION_KEYS,
} from "../services/contentApi";

const HomePageContext = createContext(null);

export function HomePageProvider({ children }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetchHomePage()
      .then((data) => {
        if (!cancelled) {
          setPage(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setPage(null);
        }
        console.error("Error fetching home page:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const sections = useMemo(() => {
    const result = {};
    HOME_SECTION_KEYS.forEach((key) => {
      result[key] = findSection(page, key);
    });
    return result;
  }, [page]);

  const value = useMemo(
    () => ({ page, sections, loading, error }),
    [page, sections, loading, error]
  );

  return (
    <HomePageContext.Provider value={value}>
      {children}
    </HomePageContext.Provider>
  );
}

export function useHomePage() {
  const ctx = useContext(HomePageContext);
  if (!ctx) {
    throw new Error("useHomePage must be used within HomePageProvider");
  }
  return ctx;
}

export function useHomeSection(key) {
  const ctx = useContext(HomePageContext);
  if (!ctx) {
    return { section: null, loading: false, fromHomePage: false };
  }
  return {
    section: ctx.sections[key] ?? null,
    loading: ctx.loading,
    fromHomePage: true,
  };
}
